/**
 * Global nav badge — visible from every authenticated route. Polls
 * ``GET /api/catalog/uncategorised-count`` every 30 seconds while the
 * tab is foregrounded, and refreshes on the ``catalog:mutation`` custom
 * window event so mutations propagate without waiting for the poll.
 * Clicking jumps to the workspace with the drift bucket scrolled into
 * view via the ``#uncategorised`` anchor.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { api } from "../api/client";

const POLL_MS = 30_000;

export const CATALOG_MUTATION_EVENT = "catalog:mutation";

export const UncategorisedBadge = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.getUncategorisedCount();
        if (!cancelled) setCount(r.count);
      } catch {
        // Quiet: drift count is best-effort cosmetic. A network blip
        // shouldn't pop a global toast.
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), POLL_MS);
    const onMutation = () => void refresh();
    window.addEventListener(CATALOG_MUTATION_EVENT, onMutation);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener(CATALOG_MUTATION_EVENT, onMutation);
    };
  }, []);

  if (count === 0) return null;
  return (
    <Tooltip title={`${count} uncategorised article${count === 1 ? "" : "s"} — click to review`}>
      <span
        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
        onClick={() => navigate("/catalog#uncategorised")}
      >
        <WarningOutlined style={{ color: "var(--ant-color-warning)" }} />
        <Badge count={count} overflowCount={99} />
      </span>
    </Tooltip>
  );
};

/**
 * Fire from anywhere in the SPA after a mutation that may have changed
 * the drift count (create / delete article, assign category). The
 * badge listens on the same custom event and refreshes immediately.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const emitCatalogMutation = () => {
  window.dispatchEvent(new Event(CATALOG_MUTATION_EVENT));
};
